import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Toolbar
} from "@mui/material";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import EditWrap from "../../commonComponent/editWrap";

export const gaugeTypeOptions = [
  { id: 1, label: "Gauge Plane" },
  { id: 2, label: "Small End" }
];

export default function EditTapperThreadRing(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== "0");

  const [gaugeType, setGaugeType] = useState(null);
  const [specification, setSpecification] = useState("");
  const [pitchDialMax, setPitchDialMax] = useState("");
  const [pitchDialMin, setPitchDialMin] = useState("");
  const [maxStep, setMaxStep] = useState("");
  const [basicStep, setBasicStep] = useState("");
  const [minStep, setMinStep] = useState("");
  const [totalLength, setTotalLength] = useState("");

  useEffect(() => {
    if (isEditing) {
      fetchDetails();
    }
  }, [id]);

  const fetchDetails = async () => {
    try {
      const res = await axiosWithToken.get(`${BASE_URL}taperThreadSizes/${id}`);
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      if (data) {
        const foundType = gaugeTypeOptions.find((opt) => opt.id === Number(data.gaugeType)) || null;
        setGaugeType(foundType);
        setSpecification(data.specification || "");
        setPitchDialMax(data.pitchDialMax !== null && data.pitchDialMax !== undefined ? data.pitchDialMax : "");
        setPitchDialMin(data.pitchDialMin !== null && data.pitchDialMin !== undefined ? data.pitchDialMin : "");
        setMaxStep(data.maxStep !== null && data.maxStep !== undefined ? data.maxStep : "");
        setBasicStep(data.basicStep !== null && data.basicStep !== undefined ? data.basicStep : "");
        setMinStep(data.minStep !== null && data.minStep !== undefined ? data.minStep : "");
        setTotalLength(data.totalLength !== null && data.totalLength !== undefined ? data.totalLength : "");
      }
    } catch (error) {
      console.error("Error fetching Taper Thread Ring details:", error);
      toast.error("Failed to load details");
    }
  };

  const handleSubmit = async () => {
    if (!gaugeType) {
      toast.error("Please select a Type");
      return;
    }
    if (!specification.trim()) {
      toast.error("Please enter Standard Specification");
      return;
    }

    const payload = {
      gaugeType: gaugeType.id,
      specification: specification.trim(),
      pitchDialMax: pitchDialMax !== "" ? parseFloat(pitchDialMax) : null,
      pitchDialMin: pitchDialMin !== "" ? parseFloat(pitchDialMin) : null,
      maxStep: maxStep !== "" ? parseFloat(maxStep) : null,
      basicStep: basicStep !== "" ? parseFloat(basicStep) : null,
      minStep: minStep !== "" ? parseFloat(minStep) : null,
      totalLength: totalLength !== "" ? String(totalLength) : null
    };

    try {
      if (isEditing) {
        await axiosWithToken.patch(`${BASE_URL}taperThreadSizes/${id}`, payload);
        toast.success("Taper Thread Ring updated successfully!");
      } else {
        await axiosWithToken.post(`${BASE_URL}taperThreadSizes`, payload);
        toast.success("Taper Thread Ring created successfully!");
      }
      setTimeout(() => {
        navigate("/master/tapperThreadRingList");
      }, 500);
    } catch (error) {
      console.error("Error saving Taper Thread Ring:", error);
      toast.error("Failed to save");
    }
  };

  return (
    <EditWrap>
      {/* Page Title */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <Typography variant="h6" component="h6" style={{ float: "left" }}>
          {isEditing ? "Edit Taper Thread Ring " : "Create New Taper Thread Ring"}
        </Typography>
      </div>

      {/* Form Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={gaugeTypeOptions}
            getOptionLabel={(option) => option.label || ""}
            value={gaugeType}
            onChange={(e, newValue) => setGaugeType(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Type *" variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <TextField
            label="Standard Specification *"
            variant="outlined"
            size="small"
            fullWidth
            value={specification}
            onChange={(e) => setSpecification(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Limits Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 1 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#DDDDDD" }}>
            <TableRow>
              <TableCell sx={{ width: "30%" }}>
                <b>PARAMETER (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "35%" }}>
                <b>SPECIFIED LIMIT MAX (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "35%" }}>
                <b>SPECIFIED LIMIT MIN (MM)</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>PITCH DIAL</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={pitchDialMax}
                  onChange={(e) => setPitchDialMax(e.target.value)}
                  sx={{ width: "60%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={pitchDialMin}
                  onChange={(e) => setPitchDialMin(e.target.value)}
                  sx={{ width: "60%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>MAX STEP</TableCell>
              <TableCell align="center" colSpan={2}>
                <TextField
                  type="number"
                  size="small"
                  value={maxStep}
                  onChange={(e) => setMaxStep(e.target.value)}
                  sx={{ width: "60%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>BASIC STEP</TableCell>
              <TableCell align="center" colSpan={2}>
                <TextField
                  type="number"
                  size="small"
                  value={basicStep}
                  onChange={(e) => setBasicStep(e.target.value)}
                  sx={{ width: "60%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>MIN STEP</TableCell>
              <TableCell align="center" colSpan={2}>
                <TextField
                  type="number"
                  size="small"
                  value={minStep}
                  onChange={(e) => setMinStep(e.target.value)}
                  sx={{ width: "60%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>TOTAL LENGTH</TableCell>
              <TableCell align="center" colSpan={2}>
                <TextField
                  size="small"
                  value={totalLength}
                  onChange={(e) => setTotalLength(e.target.value)}
                  sx={{ width: "60%" }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom Action Toolbar */}
      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginTop: "3rem",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={handleSubmit}
        >
          {isEditing ? "Update" : "Save"}
        </Button>
      </Toolbar>
      <ToastContainer />
    </EditWrap>
  );
}